import { useEffect } from 'react';
import { useMutation } from 'platform/useMutation';
import { gql, MutationHookOptions } from '@apollo/client';

import {
  SendInvitation,
  SendInvitationVariables,
} from 'sellDirect/graphql/__generated__/SendInvitation';
import { useObservability } from 'shared/helpers/observability';
import { useOfferContext } from 'sellDirect/OfferContext';

const SendInvitationMutation = gql`
  mutation SendInvitation(
    $webOfferToken: String!
    $email: String!
    $name: String!
    $signatoryId: String!
  ) {
    seller {
      sendInvitation(
        webOfferToken: $webOfferToken
        email: $email
        name: $name
        signatoryId: $signatoryId
      ) {
        id
        email
        fullName
        isAccepted
        hasExistingUser
      }
    }
  }
`;

/** TODO: Add documentation */
export const useSendInvitation = (options?: MutationHookOptions<SendInvitation>) => {
  const { offer } = useOfferContext();

  const { trackError } = useObservability();
  const [mutate, { data, loading, error }] = useMutation<SendInvitation, SendInvitationVariables>(
    SendInvitationMutation,
    {
      onCompleted: options?.onCompleted,
      onError: options?.onError,
      errorPolicy: options?.errorPolicy,
    },
  );

  const sendInvitation = (email: string, name: string, signatoryId: string) => {
    if (!offer?.leadToken) {
      trackError('missing-web-offer-token', undefined, {
        email,
        name,
        signatoryId,
        webOfferToken: offer?.leadToken,
      });
      return;
    }

    mutate({
      variables: {
        email,
        name,
        signatoryId,
        webOfferToken: offer.leadToken,
      },
    }).catch((error) => {
      trackError('send-invitation', error, {
        email,
        name,
        signatoryId,
        webOfferToken: offer.leadToken,
      });
    });
  };

  useEffect(() => {
    if (error) {
      trackError('send-invitation', error, { webOfferToken: offer?.leadToken });
    }
    // trackError does not maintain its identity across re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, offer?.leadToken]);

  return { sendInvitation, loading, data, error };
};
